import {
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Surface,
} from '@saay/blocks';
import type { ReactNode } from 'react';

/** The one dark pill action of a block (D-93 look rules), added to `buttonVariants()`. */
export const DARK_PILL = 'rounded-full bg-foreground px-5 text-background hover:bg-foreground/90';

/** A secondary pill action beside it, added to `buttonVariants({ variant: 'outline' })`. */
export const OUTLINE_PILL = 'rounded-full px-5';

/** A block of a detail page's tab panel: the v2 surface with an h3 under the page's tabs. */
export function SurfaceSheetSection({
  actions,
  children,
  id,
  title,
}: {
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly id: string;
  readonly title: ReactNode;
}) {
  return (
    <SurfaceSection
      {...(actions === undefined ? {} : { action: actions })}
      id={id}
      level={3}
      title={title}
    >
      {children}
    </SurfaceSection>
  );
}

/**
 * A titled block on the v2 grey `Surface` (D-93): a named region with its h2 title, an optional
 * muted line, one action at the end of the header, and the content.
 */
export function SurfaceSection({
  action,
  children,
  className,
  contentClassName,
  description,
  id,
  level = 2,
  title,
}: {
  readonly action?: ReactNode;
  readonly children?: ReactNode;
  readonly className?: string;
  readonly contentClassName?: string;
  readonly description?: ReactNode;
  readonly id: string;
  /** 3 inside a sheet, under the sheet's own title. */
  readonly level?: 2 | 3;
  readonly title: ReactNode;
}) {
  const Heading = level === 3 ? 'h3' : 'h2';
  return (
    <Surface aria-labelledby={id} role="region" {...(className === undefined ? {} : { className })}>
      <CardHeader>
        <CardTitle>
          <Heading id={id}>{title}</Heading>
        </CardTitle>
        {description === undefined ? null : <CardDescription>{description}</CardDescription>}
        {action === undefined ? null : <CardAction>{action}</CardAction>}
      </CardHeader>
      {children === undefined || children === null ? null : (
        <CardContent className={contentClassName ?? 'flex flex-col gap-3'}>{children}</CardContent>
      )}
    </Surface>
  );
}
